// What a tune link opens (Oct 2026). A link names a tune by title and credit,
// `?tune=Title&by=Composer` with `&source=realbook` for a tune only the Real
// Book carries, and opens ONE version of it, looked for in a fixed order: your
// saved copy, a score that ships with the app, a lead sheet, then a chord chart
// (the list you imported first, then the chord charts that ship with the app).
// The workbench's link loader (useTuneUrl in SequenceBuilderPage) walks the
// same order with the same match, and the home page's cards ask this module
// which version to add to a playlist, so a card files the version its own link
// opens.

import type { IRealIndex } from '@/music/ireal/parse';
import type { Score } from '@/music/score/types';
import { useStore } from '@/state/store';
import type { PlaylistTuneRef } from '@/storage/playlists';
import { BUNDLED_SCORES } from './bundledScores';
import { openbookLibrary, type OpenbookLibraryEntry } from './openbookLibrary';
import { realbookLibrary, type RealbookLibraryEntry } from './realbookLibrary';
import type { ScoreCatalogEntry } from './scoreCatalog';
import { loadBundledTuneSearchIndex, persistedImport } from './useTuneSearchSources';

/** The tune a link names. */
export interface TuneLinkTarget {
  title: string;
  composer?: string;
  /** Only the Real Book carries it, so the link names the source. */
  source?: 'realbook';
}

const fold = (text: string | undefined) => (text ?? '').trim().toLowerCase();

/**
 * The entry a link names in one list: the same title, case and outer spaces
 * aside. The credit decides between tunes sharing a title; with no credit
 * given, or none agreeing, the first one stands.
 */
export function linkMatch<T>(
  entries: readonly T[],
  title: string,
  composer: string | undefined,
  read: (entry: T) => { title: string; composer?: string },
): T | undefined {
  const wanted = fold(title);
  if (!wanted) return undefined;
  const matches = entries.filter((entry) => fold(read(entry).title) === wanted);
  const by = composer === undefined ? undefined : fold(composer);
  return matches.find((entry) => fold(read(entry).composer) === by) ?? matches[0];
}

// How each kind of version is referenced: the same shapes the tune browser's
// rows carry (tuneSources), so a tune added from a card and the same tune
// added from the search are one row to "already in this playlist".

export const realbookRef = (entry: RealbookLibraryEntry): PlaylistTuneRef => ({
  kind: 'realbook',
  id: entry.id,
  file: entry.file,
  title: entry.title,
  composer: entry.composer,
});

export const openbookRef = (entry: OpenbookLibraryEntry): PlaylistTuneRef => ({
  kind: 'openbook',
  id: entry.id,
  file: entry.file,
  title: entry.title,
  composer: entry.composer,
});

export const bundledScoreRef = (entry: ScoreCatalogEntry): PlaylistTuneRef => ({
  kind: 'score',
  id: entry.record.id,
  revisionId: entry.revision.id,
  title: entry.summary.title,
  composer: entry.summary.composer,
});

export const chordChartRef = (
  index: IRealIndex,
  song: IRealIndex['songs'][number],
): PlaylistTuneRef => ({
  kind: 'ireal',
  title: song.title,
  composer: song.composer ?? '',
  scheme: index.scheme,
  chunk: song.chunk,
});

/** The lists a link looks in, in the order it looks. */
export interface TuneLinkLists {
  savedCharts: readonly Score[];
  scores: readonly ScoreCatalogEntry[];
  openbook: readonly OpenbookLibraryEntry[];
  realbook: readonly RealbookLibraryEntry[];
  /** Chord charts, first list first: your import, then the shipped charts. */
  chordCharts: readonly (IRealIndex | null | undefined)[];
}

/**
 * The version a link opens, as a playlist row names it; null when no list
 * holds the tune, or when the copy it opens cannot be referenced (a saved
 * tune with no id, which the saved-tune guard no longer lets through).
 */
export function tuneLinkRef(target: TuneLinkTarget, lists: TuneLinkLists): PlaylistTuneRef | null {
  const { title, composer } = target;
  if (target.source === 'realbook') {
    const hit = linkMatch(lists.realbook, title, composer, (entry) => entry);
    if (hit) return realbookRef(hit);
  }
  const saved = linkMatch(lists.savedCharts, title, composer, (score) => score);
  if (saved)
    return saved.id
      ? { kind: 'saved', id: saved.id, title: saved.title, composer: saved.composer }
      : null;
  const score = linkMatch(lists.scores, title, composer, (entry) => entry.summary);
  if (score) return bundledScoreRef(score);
  const lead = linkMatch(lists.openbook, title, composer, (entry) => entry);
  if (lead) return openbookRef(lead);
  for (const index of lists.chordCharts) {
    if (!index) continue;
    const song = linkMatch(index.songs, title, composer, (entry) => entry);
    if (song) return chordChartRef(index, song);
  }
  return null;
}

/**
 * The version a link opens, loading only what it needs: the lead sheets'
 * list (fetched once a session), and the shipped chord charts only when no
 * other list has the tune. Throws when a list it needs cannot be loaded.
 */
export async function resolveTuneLinkRef(target: TuneLinkTarget): Promise<PlaylistTuneRef | null> {
  const [openbook, realbook] = await Promise.all([
    openbookLibrary(),
    target.source === 'realbook' ? realbookLibrary() : Promise.resolve([]),
  ]);
  const state = useStore.getState();
  const lists: TuneLinkLists = {
    savedCharts: state.savedCharts,
    scores: BUNDLED_SCORES,
    openbook,
    realbook,
    chordCharts: [state.playlist ?? persistedImport()],
  };
  const found = tuneLinkRef(target, lists);
  if (found) return found;
  const shipped = await loadBundledTuneSearchIndex();
  return tuneLinkRef(target, { ...lists, chordCharts: [...lists.chordCharts, shipped] });
}
